import React from 'react';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Shield, Lock, Eye, Camera, CloudOff, Users, Smartphone, FileText, Trash2 } from 'lucide-react';

const PrivacyPolicy = () => {
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  const sections = [
    {
      id: 1,
      icon: Eye,
      title: "Information We Do NOT Collect (Keystroke Privacy)",
      subtitle: "Your privacy is our primary concern. As a general rule:",
      content: [
        { label: "No Keylogging", text: "We do not log, record, or transmit your general keystrokes, passwords, credit card numbers, or chat messages to our servers." },
        { label: "Local Processing", text: "Your personal typing history, learned words, custom dictionary, and text shortcuts are stored locally and securely on your device. We do not have access to this data." },
        { label: "AI Features", text: "The AI only processes text that YOU manually paste into a feature. We never read your messages, notifications, or keyboard input automatically. You are always in control." }
      ]
    },
    {
      id: 2,
      icon: Camera,
      title: "Device Permissions",
      subtitle: "To provide advanced productivity features, Typeit 4U requires specific device permissions. We only access these features when you explicitly trigger them.",
      content: [
        { label: "Camera (OCR / Text Scanner)", text: "When you tap the scanner icon, the App uses your camera to extract text (e.g., from visiting cards or documents). This process is handled 100% offline on your device using Google ML Kit. No images or photos are ever saved, stored, or transmitted to our servers." },
        { label: "Microphone (Voice Typing)", text: "When you tap the microphone icon, the App captures your voice to convert it to text. This is processed using Android's built-in Speech Recognizer. We do not record, store, or transmit your audio data." },
        { label: "Location", text: "When you tap the location sharing icon, the App briefly accesses your device's GPS or network location to generate a map link for you to paste. Location data is only accessed at the exact moment you click the button and is never tracked in the background or sent to our servers." },
        { label: "Storage / Files (Attachments)", text: "To use the attachment panel, the App accesses your local files (such as PDFs or images) so you can insert them into chats. We do not scan, collect, or upload your personal files." },
        { label: "Clipboard Access", text: "The App reads your clipboard data strictly to provide the 'Clipboard History' quick-paste feature. Your clipboard history is saved locally on your device and is never transmitted over the internet. The clipboard feature is triggered only when you open the Clipboard Panel. We do not monitor your clipboard passively." }
      ]
    },
    {
      id: 3,
      icon: CloudOff,
      title: "AI Features and Internet Connectivity",
      subtitle: "Typeit 4U includes optional AI features (such as Rephrase, Improve, Translate, Create Post, Auto Reply, Email Reply, etc.).",
      content: [
        { label: "How it works", text: "When you manually paste text and tap an AI action button, only that specific text you selected is transmitted securely via HTTPS to our server and processed by OpenAI (our third-party AI provider) to generate the response." },
        { label: "Key Point", text: "We never read your keyboard input, messages, or notifications automatically. The AI is triggered only by your explicit action of pasting text and tapping a feature button." },
        { label: "Ephemeral Processing", text: "This text input is processed in real-time and is not stored permanently on our servers or by OpenAI for model training." },
        { label: "Purpose", text: "We process this data solely to return the requested text modification to you." }
      ]
    },
    {
      id: 4,
      icon: FileText,
      title: "Third-Party Services",
      subtitle: "The App utilizes third-party services that may collect information used to identify your device or process specific tasks:",
      content: [
        { label: "Google Play Services", text: "Used for core Android functionality." },
        { label: "Google ML Kit", text: "Used locally on your device for offline Text Recognition (OCR)." },
        { label: "OpenAI API", text: "Used strictly for processing text when you trigger AI features (Improve, Translate, etc.)." }
      ]
    },
    {
      id: 5,
      icon: Smartphone,
      title: "Device Information & Usage Limits",
      subtitle: "To manage free usage quotas (e.g., daily limits on free AI features) and check subscription status, we collect a unique Device Identifier (Android ID).",
      content: [
        { label: "Purpose", text: "This ID is used strictly to enforce usage limits on free features and verify Premium/Pro status." },
        { label: "No Tracking", text: "We do not use this identifier for advertising, marketing, or tracking your activity across other apps." }
      ]
    },
    {
      id: 6,
      icon: Lock,
      title: "Data Security",
      subtitle: null,
      content: [
        { label: null, text: "We value your trust in providing us with your personal data and use commercially acceptable means of protecting it. We use HTTPS encryption for all data transmission." },
        { label: null, text: "All AI requests are transmitted over HTTPS with end-to-end encryption. OCR (image scanning) is processed entirely on your device and no image data is ever sent to our servers." },
        { label: null, text: "Remember that no method of transmission over the internet or method of electronic storage is 100% secure, and we cannot guarantee its absolute security." }
      ]
    },
    {
      id: 7,
      icon: Trash2,
      title: "Data Deletion Request",
      subtitle: "To request the deletion of your data (Device ID or AI processing logs), please provide your Device ID or the email used for your Pro subscription.",
      content: [
        { label: "Email", text: "support@typeit4u.com" },
        { label: "Response Time", text: "We will process your request within 48-72 hours." }
      ]
    },
    {
      id: 8,
      icon: Users,
      title: "Children's Privacy",
      subtitle: null,
      content: [
        { label: null, text: "These Services do not address anyone under the age of 13. We do not knowingly collect personally identifiable information from children under 13." }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full mb-6">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">No Keystroke Logging</span>
            </div>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
              style={{ color: '#0A0A0A' }}
            >
              Privacy Policy
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-4">
              We are committed to protecting your privacy and ensuring you have a secure typing experience. This Privacy Policy explains how we collect, use, and protect your information.
            </p>
            <p className="text-sm text-gray-500">Last Updated: February 24, 2026</p>
            <div className="mt-6 p-4 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 inline-block">
              <p className="text-sm text-gray-700">
                <strong>By using Typeit 4U, you consent to the data practices described in this policy.</strong>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container max-w-4xl">
          <div className="space-y-12">
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255, 140, 66, 0.1) 0%, rgba(255, 107, 157, 0.1) 100%)'
                      }}
                    >
                      <Icon className="w-6 h-6" style={{ color: '#FF6B9D' }} />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl sm:text-2xl font-semibold mb-2" style={{ color: '#0A0A0A' }}>
                        {section.id}. {section.title}
                      </h2>
                      {section.subtitle && (
                        <p className="text-gray-600 mb-4 italic">{section.subtitle}</p>
                      )}
                      <div className="space-y-3">
                        {section.content.map((item, idx) => (
                          <p key={idx} className="text-gray-700 leading-relaxed">
                            {item.label && <strong>{item.label}: </strong>}
                            {item.text}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Additional Sections */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 border border-gray-200"
            >
              <h2 className="text-xl sm:text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                9. Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                We may update our Privacy Policy from time to time. Thus, you are advised to review this page periodically for any changes.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We will notify you of any changes by posting the new Privacy Policy on this page.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-8 border border-orange-200"
            >
              <h2 className="text-xl sm:text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                10. Contact Us
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions or suggestions about our Privacy Policy, do not hesitate to contact us.
              </p>
              <p className="text-gray-700 leading-relaxed mb-2">
                <strong>Email:</strong> support@deyvainfotech.com
              </p>
              <p className="text-gray-700 leading-relaxed text-sm">
                <strong>Note:</strong> Typeit4U is a product of Deyvainfotech. For product-specific queries, you may also reach us at: support@typeit4u.com
              </p>
            </motion.div>
          </div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mt-16 text-center"
          >
            <p className="text-gray-600 mb-6">Have questions about our privacy practices?</p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '26px',
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
